import React, { FC } from "react";
import { Link } from "react-router-dom";

import { existsAndNotEmpty } from "stentor-utils";
import { IconButton, TitleHeader, Tooltip, Button, PageAlert } from "@xapp/components";

import { AppReferenceIdInput, IntegrationType } from "generated/graphql";

import { IntegrationsListDialog } from "./IntegrationsListDialog";
import { IntegrationList, IntegrationListProps } from "./IntegrationList";
import { IntegrationConnectionProps } from "./BasicIntegrationCard";
import { ReferenceIdSettings } from "./ReferenceIdSettings";

export interface IntegrationViewProps extends IntegrationListProps {
    /**
     * Add Integration dialog is active
     */
    active?: boolean;
    /**
     * User has requested to add an integration. (SHOW THE DIALOG)
     */
    onAdd?(): void;
    /**
     * Dialog cancel
     */
    onCancel?(): void;
    /**
     * User is attempting to connect an integration
     *
     * This is used for the non-OAUTH integrations: ServiceTitan, Surefire, Housecall Pro
     */
    onConnect?(props: IntegrationConnectionProps): void;
    /**
     * User requested to disconnect the integration.
     */
    onDisconnect?(type: IntegrationType): void;
    /**
     * Reference ID for external service
     */
    referenceId?: AppReferenceIdInput | null;
    /**
     * Whether the reference ID is currently being saved
     */
    savingReferenceId?: boolean;
    /**
     * Callback to save the reference ID
     */
    onSaveReferenceId?(referenceId: AppReferenceIdInput | null): void;
}

export const IntegrationsView: FC<IntegrationViewProps> = (props: IntegrationViewProps) => {
    const {
        active,
        app,
        data,
        limitIntegrations,
        omitIntegrations,
        onAdd,
        onCancel,
        onSelected,
        selected,
        supportsIntegrations,
        referenceId,
        savingReferenceId,
        onSaveReferenceId,
    } = props;

    const selectionMode = existsAndNotEmpty(selected);

    if (selectionMode) {
        // make sure we have a callback
        if (typeof onSelected !== "function") {
            throw new Error("onSelected must be a function when selected array is provided");
        }
    }

    // What does linked and active mean?
    // isLinked - User has set up a link to jobber
    // isActive - The link works

    const isBoberdooLinked: boolean = data?.boberdoo?.isLinked ?? false;
    const isLACRMLinked: boolean = data?.lacrm?.isLinked;
    const isJobberLinked: boolean = data?.jobber?.isLinked;
    const isServiceTitanLinked: boolean = data?.servicetitan?.isLinked;
    const isHousecallProLinked: boolean = data?.housecallpro?.isLinked;
    const isFieldPulseLinked: boolean = data?.fieldpulse?.isLinked;
    const isFieldRoutesLinked: boolean = data?.fieldroutes?.isLinked;
    const isSurefireLocalLinked: boolean = data?.surefire?.isLinked;
    const isServiceminderLinked: boolean = data?.serviceminder?.isLinked;
    const isContractorAppointmentsLinked: boolean = data?.contractor_appointments?.isLinked;
    const isWebhookLinked: boolean = data?.webhook?.isLinked;

    // If limitIntegrations is null/undefined, allow all integrations
    // If it's an array, use those specific integrations
    let availableIntegrations: string[];

    if (limitIntegrations === null || limitIntegrations === undefined) {
        // Allow all integrations when null/undefined
        availableIntegrations = [
            "boberdoo",
            "lacrm",
            "jobber",
            "surefire",
            "servicetitan",
            "serviceminder",
            "housecallpro",
            "zapier",
            "fieldpulse",
            "fieldroutes",
            "contractor_appointments",
            "webhook",
        ];
    } else if (Array.isArray(limitIntegrations)) {
        // Use the specific allowed integrations
        availableIntegrations = limitIntegrations;
    } else {
        // Fallback to default list if something unexpected
        availableIntegrations = [
            "boberdoo",
            "lacrm",
            "jobber",
            "surefire",
            "servicetitan",
            "serviceminder",
            "housecallpro",
            "zapier",
            "fieldpulse",
            "fieldroutes",
            "contractor_appointments",
            "webhook",
        ];
    }

    if (existsAndNotEmpty(omitIntegrations)) {
        // Create a set for faster lookup
        const omitSet = new Set(omitIntegrations);

        // Filter out the omitted integrations
        availableIntegrations = availableIntegrations.filter((integration) => {
            if (omitSet.has(integration)) {
                return false;
            }
            return true;
        });
    }

    const linkedIntegrations: string[] = [];

    if (isBoberdooLinked) {
        linkedIntegrations.push("boberdoo");
    }

    if (isLACRMLinked) {
        linkedIntegrations.push("lacrm");
    }

    if (isJobberLinked) {
        linkedIntegrations.push("jobber");
    }

    if (isServiceTitanLinked) {
        linkedIntegrations.push("servicetitan");
    }

    if (isHousecallProLinked) {
        linkedIntegrations.push("housecallpro");
    }

    if (isFieldPulseLinked) {
        linkedIntegrations.push("fieldpulse");
    }

    if (isFieldRoutesLinked) {
        linkedIntegrations.push("fieldroutes");
    }

    if (isSurefireLocalLinked) {
        linkedIntegrations.push("surefire");
    }

    if (isServiceminderLinked) {
        linkedIntegrations.push("serviceminder");
    }

    if (isContractorAppointmentsLinked) {
        linkedIntegrations.push("contractor_appointments");
    }

    if (isWebhookLinked) {
        linkedIntegrations.push("webhook");
    }

    const hasLinkedIntegration: boolean = existsAndNotEmpty(linkedIntegrations);

    // Filter out already linked integrations from the dialog
    const unlinkedIntegrations = availableIntegrations.filter(
        (integration) => !linkedIntegrations.includes(integration)
    );

    return (
        <>
            <TitleHeader value={"Integrations"}>
                <Tooltip content={supportsIntegrations ? "Add Integration" : "Not Supported"}>
                    <IconButton
                        iconName="library_add"
                        onClick={onAdd}
                        color="primary"
                        disabled={!supportsIntegrations}
                    />
                </Tooltip>
            </TitleHeader>
            {!supportsIntegrations ? (
                <div>
                    <PageAlert background="orange">
                        <p>
                            Integrations are not supported on your current plan. Please upgrade to one of our paid tiers
                            to integrate with 3rd party service providers.
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", rowGap: "10px", alignItems: "center" }}>
                            <Link to={`/${app.organizationId}/${app.appId}/upgrade`}>
                                <Button variation="primary" label="Upgrade Plan" />
                            </Link>
                        </div>
                    </PageAlert>
                </div>
            ) : (
                <></>
            )}

            {!hasLinkedIntegration ? (
                <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                    <p>No integrations connected yet. Click the + button to add one.</p>
                </div>
            ) : (
                <IntegrationList
                    {...props}
                    app={app}
                    data={data}
                    onboardingMode={false}
                    onConnect={props.onConnect}
                    disabled={!supportsIntegrations}
                    onDisconnect={props.onDisconnect}
                />
            )}

            <IntegrationsListDialog
                app={app}
                active={active}
                disabled={!supportsIntegrations}
                integrations={unlinkedIntegrations}
                onCancel={onCancel}
                onConnect={props.onConnect}
            />

            <ReferenceIdSettings
                referenceId={referenceId}
                saving={savingReferenceId}
                onSave={onSaveReferenceId}
            />
        </>
    );
};
